import {
    CartItem,
    ProductInstance,
    ProductOption,
    TicketOption,
    TicketOptionWithQuantity,
    TicketType,
    PackageTicketOptionWithQuantity,
} from '@repo/types';
import { TimeSlotType } from '@repo/widget-utils/TimeSlotType';

export type PackageOrSingleQuantities = TicketOptionWithQuantity | PackageTicketOptionWithQuantity;

export type BookingState<T extends PackageOrSingleQuantities = TicketOptionWithQuantity> = {
    quantities: T[];
    /** All ticket options from the product (for showing unavailable tickets) */
    defaultTicketOptions: TicketOption[];
    selectedProducts: ProductInstance[] | undefined;
    selectedTimeSlot: TimeSlotType | undefined;
    selectedTicketType: TicketType | undefined;
    /** Selected product option for OCTO products with multiple options */
    selectedProductOption: ProductOption | undefined;
    newCartItem: CartItem | null;
    cartItems: CartItem[];
    isLoadingPrices: boolean;
    attemptedBooking: boolean;
    hasChosenDate: boolean;
    hasChangedQuantities: boolean;
};

type SetQuantitiesAction<T extends PackageOrSingleQuantities> = {
    type: 'setQuantities';
    value: BookingState<T>['quantities'];
};

type SetSelectedProductsAction = {
    type: 'setSelectedProducts';
    value: BookingState['selectedProducts'];
};

type SetSelectedTimeSlotAction = {
    type: 'setSelectedTimeSlot';
    value: BookingState['selectedTimeSlot'];
};

type SetSelectedTicketTypeAction = {
    type: 'setSelectedTicketType';
    value: BookingState['selectedTicketType'];
};

type SetSelectedProductOptionAction = {
    type: 'setSelectedProductOption';
    value: BookingState['selectedProductOption'];
};

type SetNewCartItemAction = {
    type: 'setNewCartItem';
    value: BookingState['newCartItem'];
};

type SetCartItemsAction = {
    type: 'setCartItems';
    value: BookingState['cartItems'];
};

type SetIsLoadingPricesAction = {
    type: 'setIsLoadingPrices';
    value: BookingState['isLoadingPrices'];
};

type SetAttemptedBookingAction = {
    type: 'setAttemptedBooking';
    value: BookingState['attemptedBooking'];
};

type SetHasChosenDateAction = {
    type: 'setHasChosenDate';
    value: BookingState['hasChosenDate'];
};

type SetHasChangedQuantitiesAction = {
    type: 'setHasChangedQuantities';
    value: BookingState['hasChangedQuantities'];
};

type SetDefaultTicketOptionsAction = {
    type: 'setDefaultTicketOptions';
    value: BookingState['defaultTicketOptions'];
};

export type BookingActions<T extends PackageOrSingleQuantities> =
    | SetQuantitiesAction<T>
    | SetSelectedProductsAction
    | SetSelectedTimeSlotAction
    | SetSelectedTicketTypeAction
    | SetSelectedProductOptionAction
    | SetNewCartItemAction
    | SetCartItemsAction
    | SetIsLoadingPricesAction
    | SetAttemptedBookingAction
    | SetHasChosenDateAction
    | SetHasChangedQuantitiesAction
    | SetDefaultTicketOptionsAction;

export const initialState = <T extends PackageOrSingleQuantities>(
    init: Omit<
        BookingState<T>,
        | 'cartItems'
        | 'newCartItem'
        | 'isLoadingPrices'
        | 'attemptedBooking'
        | 'hasChosenDate'
        | 'hasChangedQuantities'
        | 'defaultTicketOptions'
        | 'selectedProductOption'
    >,
): BookingState<T> => ({
    quantities: init.quantities,
    defaultTicketOptions: [],
    selectedProducts: init.selectedProducts,
    selectedTimeSlot: init.selectedTimeSlot,
    selectedTicketType: init.selectedTicketType,
    selectedProductOption: undefined,
    newCartItem: null,
    cartItems: [],
    isLoadingPrices: false,
    attemptedBooking: false,
    hasChosenDate: false,
    hasChangedQuantities: false,
});

// Not using immer/produce() here, because some attributes are too
// recursive/big for immer to handle.
export const bookingReducer = <T extends PackageOrSingleQuantities>(
    state: BookingState<T>,
    { type, value }: BookingActions<T>,
): BookingState<T> => {
    const newState = { ...state };
    let prev: unknown;

    switch (type) {
        case 'setQuantities':
            prev = state.quantities;
            newState.quantities = value;
            break;
        case 'setSelectedProducts':
            prev = state.selectedProducts;
            newState.selectedProducts = value;
            break;
        case 'setSelectedTimeSlot':
            prev = state.selectedTimeSlot;
            newState.selectedTimeSlot = value;
            break;
        case 'setSelectedTicketType':
            prev = state.selectedTicketType;
            newState.selectedTicketType = value;
            break;
        case 'setSelectedProductOption':
            prev = state.selectedProductOption;
            newState.selectedProductOption = value;
            break;
        case 'setNewCartItem':
            prev = state.newCartItem;
            newState.newCartItem = value;
            break;
        case 'setCartItems':
            prev = state.cartItems;
            newState.cartItems = value;
            break;
        case 'setIsLoadingPrices':
            prev = state.isLoadingPrices;
            newState.isLoadingPrices = value;
            break;
        case 'setAttemptedBooking':
            prev = state.attemptedBooking;
            newState.attemptedBooking = value;
            break;
        case 'setHasChosenDate':
            prev = state.hasChosenDate;
            newState.hasChosenDate = value;
            break;
        case 'setHasChangedQuantities':
            prev = state.hasChangedQuantities;
            newState.hasChangedQuantities = value;
            break;
        case 'setDefaultTicketOptions':
            prev = state.defaultTicketOptions;
            newState.defaultTicketOptions = value;
            break;
    }

    if (prev === value) return state;

    return newState;
};

export function setQuantities<T extends PackageOrSingleQuantities = TicketOptionWithQuantity>(
    value: SetQuantitiesAction<T>['value'],
): SetQuantitiesAction<T> {
    return { type: 'setQuantities', value };
}

export function setSelectedProducts(
    value: SetSelectedProductsAction['value'],
): SetSelectedProductsAction {
    return { type: 'setSelectedProducts', value };
}

export function setSelectedTimeSlot(
    value: SetSelectedTimeSlotAction['value'],
): SetSelectedTimeSlotAction {
    return { type: 'setSelectedTimeSlot', value };
}

export function setSelectedTicketType(
    value: SetSelectedTicketTypeAction['value'],
): SetSelectedTicketTypeAction {
    return { type: 'setSelectedTicketType', value };
}

export function setSelectedProductOption(
    value: SetSelectedProductOptionAction['value'],
): SetSelectedProductOptionAction {
    return { type: 'setSelectedProductOption', value };
}

export function setNewCartItem(value: SetNewCartItemAction['value']): SetNewCartItemAction {
    return { type: 'setNewCartItem', value };
}

export function setCartItems(value: SetCartItemsAction['value']): SetCartItemsAction {
    return { type: 'setCartItems', value };
}

export function setIsLoadingPrices(
    value: SetIsLoadingPricesAction['value'],
): SetIsLoadingPricesAction {
    return { type: 'setIsLoadingPrices', value };
}

export function setAttemptedBooking(
    value: SetAttemptedBookingAction['value'],
): SetAttemptedBookingAction {
    return { type: 'setAttemptedBooking', value };
}

export function setHasChosenDate(value: SetHasChosenDateAction['value']): SetHasChosenDateAction {
    return { type: 'setHasChosenDate', value };
}

export function setHasChangedQuantities(
    value: SetHasChangedQuantitiesAction['value'],
): SetHasChangedQuantitiesAction {
    return { type: 'setHasChangedQuantities', value };
}

export function setDefaultTicketOptions(
    value: SetDefaultTicketOptionsAction['value'],
): SetDefaultTicketOptionsAction {
    return { type: 'setDefaultTicketOptions', value };
}
